/**
 * Vibe platform issue codes — one file per code, composed here.
 *
 * ## Why this one is a directory when every other registry is a file
 *
 * The provider registries are small and stable; this one is neither. It reached
 * 19 codes in a single object literal, which made it the file every Vibe alarm
 * ticket had to edit — so detector work and remedy work could only ever happen
 * one branch at a time, and the queue was an artifact of the file layout rather
 * than of anything the codes actually share.
 *
 * One file per code removes that. Two people can give two alarms a remedy
 * without touching the same file, and a code's reasoning sits next to the code
 * instead of in a 500-line scroll.
 *
 * ## Adding a code
 *
 * Add `<kebab-name>.ts` exporting a single `as const satisfies IssueDefinition`,
 * then add it to the spread below. Forgetting the second step is the one failure
 * this layout introduces that the single file could not have, so it is gated:
 * `index.test.ts` reads the directory and fails if a code file is not composed
 * here, or if a composed code has no file.
 *
 * ## Order is meaningful
 *
 * The groups below are ordered as the subsystems layer, and the banner comments
 * refer to their neighbours ("the four above", "these two"). Keep new codes
 * inside their group.
 */
import type { IssueDefinition } from "../../issue-definition";
import { VIBE_APP_BACKUP_JOB_FAILING } from "./app-backup-job-failing";
import { VIBE_APP_BACKUP_STALE } from "./app-backup-stale";
import { VIBE_APP_EDGE_UNROUTED } from "./app-edge-unrouted";
import { VIBE_APP_RESTORE_DRILL_FAILED } from "./app-restore-drill-failed";
import { VIBE_APP_RESTORE_DRILL_STALE } from "./app-restore-drill-stale";
import { VIBE_AUDIT_EXPORT_STALE } from "./audit-export-stale";
import { VIBE_BUILD_EXECUTOR_QUEUE_SLO_BREACHED } from "./build-executor-queue-slo-breached";
import { VIBE_BUILD_INFRA_FAILURE_SLO_BREACHED } from "./build-infra-failure-slo-breached";
import { VIBE_BUILD_ORPHAN_CENSUS_UNOBSERVED } from "./build-orphan-census-unobserved";
import { VIBE_BUILD_ORPHANS_PERSISTENT } from "./build-orphans-persistent";
import { VIBE_BUILD_QUEUE_SLO_BREACHED } from "./build-queue-slo-breached";
import { VIBE_COST_SAFETY_SOFT_LIMIT_APPROACHING } from "./cost-safety-soft-limit-approaching";
import { VIBE_COST_SAFETY_SOFT_LIMIT_CROSSED } from "./cost-safety-soft-limit-crossed";
import { VIBE_FORGEJO_REPOSITORY_MISSING } from "./forgejo-repository-missing";
import { VIBE_FORGEJO_VOLUME_ENCRYPTION_UNOBSERVABLE } from "./forgejo-volume-encryption-unobservable";
import { VIBE_GIT_HOST_REPO_VOLUME_NEAR_FULL } from "./git-host-repo-volume-near-full";
import { VIBE_GIT_HOST_REPO_VOLUME_UNMEASURED } from "./git-host-repo-volume-unmeasured";
import { VIBE_PLATFORM_DR_CONVERGE_FAILING } from "./platform-dr-converge-failing";
import { VIBE_PLATFORM_DR_STATE_DRIFT_DETECTED } from "./platform-dr-state-drift-detected";
import { VIBE_PROVISIONER_JOB_OOM_KILLED } from "./provisioner-job-oom-killed";
import { VIBE_REGION_QUOTA_NEAR_LIMIT } from "./region-quota-near-limit";
import { VIBE_REGION_QUOTA_UNMEASURED } from "./region-quota-unmeasured";
import { VIBE_TENANT_AURORA_BACKUP_JOB_FAILING } from "./tenant-aurora-backup-job-failing";
import { VIBE_TENANT_AURORA_BACKUP_STALE } from "./tenant-aurora-backup-stale";
import { VIBE_TENANT_AURORA_DR_COPY_STALE } from "./tenant-aurora-dr-copy-stale";
import { VIBE_TENANT_AURORA_RESTORE_DRILL_FAILED } from "./tenant-aurora-restore-drill-failed";
import { VIBE_TENANT_AURORA_RESTORE_DRILL_MISCONFIGURED } from "./tenant-aurora-restore-drill-misconfigured";
import { VIBE_TENANT_AURORA_RESTORE_DRILL_STALE } from "./tenant-aurora-restore-drill-stale";
import { VIBE_TENANT_BACKUP_JOB_FAILING } from "./tenant-backup-job-failing";
import { VIBE_TENANT_BACKUP_STALE } from "./tenant-backup-stale";
import { VIBE_TENANT_CLUSTER_LIVE_DRIFT_UNOBSERVED } from "./tenant-cluster-live-drift-unobserved";
import { VIBE_TENANT_CLUSTER_OUT_OF_BAND_DRIFT } from "./tenant-cluster-out-of-band-drift";
import { VIBE_TENANT_CLUSTER_UNOBSERVABLE } from "./tenant-cluster-unobservable";
import { VIBE_TENANT_DR_COPY_STALE } from "./tenant-dr-copy-stale";
import { VIBE_TENANT_EDGE_TLS_VERIFICATION_FAILED } from "./tenant-edge-tls-verification-failed";
import { VIBE_TENANT_FORGEJO_VOLUME_AMBIGUOUS } from "./tenant-forgejo-volume-ambiguous";
import { VIBE_TENANT_FORGEJO_VOLUME_FAILED } from "./tenant-forgejo-volume-failed";
import { VIBE_TENANT_FORGEJO_VOLUME_NOT_TENANT_ENCRYPTED } from "./tenant-forgejo-volume-not-tenant-encrypted";
import { VIBE_TENANT_GIT_EDGE_PROXIED } from "./tenant-git-edge-proxied";
import { VIBE_TENANT_GIT_EDGE_UNOBSERVABLE } from "./tenant-git-edge-unobservable";
import { VIBE_TENANT_GIT_HOST_NODE_UNHEALTHY } from "./tenant-git-host-node-unhealthy";
import { VIBE_TENANT_NODE_HEALTH_UNOBSERVED } from "./tenant-node-health-unobserved";
import { VIBE_TENANT_NODE_UNHEALTHY } from "./tenant-node-unhealthy";
import { VIBE_TENANT_RESTORE_DRILL_FAILED } from "./tenant-restore-drill-failed";
import { VIBE_TENANT_RESTORE_DRILL_STALE } from "./tenant-restore-drill-stale";
import { VIBE_TENANT_RESTORE_DRILL_VALIDATOR_LAUNCH_REFUSED } from "./tenant-restore-drill-validator-launch-refused";
import { VIBE_ZONE_TOTAL_TLS_DISABLED } from "./zone-total-tls-disabled";

export const VIBE_ISSUES = {
  // ── The tenant's public edge: is it serving, and by which path ──
  VIBE_TENANT_EDGE_TLS_VERIFICATION_FAILED,
  VIBE_TENANT_GIT_EDGE_PROXIED,
  VIBE_TENANT_GIT_EDGE_UNOBSERVABLE,
  VIBE_ZONE_TOTAL_TLS_DISABLED,
  // One app, running, that its own tenant's edge has no route to.
  VIBE_APP_EDGE_UNROUTED,

  // ── Whether the reconcile loop can see the cluster at all ──
  VIBE_TENANT_CLUSTER_UNOBSERVABLE,
  VIBE_TENANT_CLUSTER_OUT_OF_BAND_DRIFT,
  VIBE_TENANT_CLUSTER_LIVE_DRIFT_UNOBSERVED,

  // ── The tenant's Nomad nodes and its git host's repository volume, as the
  //    node-health sweep observes them. Each "we looked and it is wrong" code
  //    has an "we could not look" pair beside it, and while the pair stands
  //    the first is HELD rather than retracted — an unobserved node or an
  //    unmeasured volume supports no claim that anything cleared. ──
  VIBE_TENANT_NODE_UNHEALTHY,
  VIBE_TENANT_GIT_HOST_NODE_UNHEALTHY,
  VIBE_TENANT_NODE_HEALTH_UNOBSERVED,
  VIBE_GIT_HOST_REPO_VOLUME_NEAR_FULL,
  VIBE_GIT_HOST_REPO_VOLUME_UNMEASURED,

  // ── The tenant's build jobs against their objectives (queue time, the
  //    executor's own queue, infra-caused failures) and its build-orphan
  //    census — the zombie-build class, measured. The census has the same
  //    observed/unobserved pair. ──
  VIBE_BUILD_QUEUE_SLO_BREACHED,
  VIBE_BUILD_EXECUTOR_QUEUE_SLO_BREACHED,
  VIBE_BUILD_INFRA_FAILURE_SLO_BREACHED,
  VIBE_BUILD_ORPHANS_PERSISTENT,
  VIBE_BUILD_ORPHAN_CENSUS_UNOBSERVED,

  // ── The shared platform-dr vault — fleet infra, not any one tenant's ──
  VIBE_PLATFORM_DR_CONVERGE_FAILING,
  VIBE_PLATFORM_DR_STATE_DRIFT_DETECTED,

  // ── The provisioner Job's own substrate: did the runner survive its own apply ──
  VIBE_PROVISIONER_JOB_OOM_KILLED,

  // ── AWS account quota headroom for what a tenant stack consumes — the
  //    proactive half of NEX-4213, so exhaustion is rare rather than fatal ──
  //    The pair is "we looked and it is running out" beside "we could not
  //    look, so the first alarm is being held rather than re-derived".
  VIBE_REGION_QUOTA_NEAR_LIMIT,
  VIBE_REGION_QUOTA_UNMEASURED,

  // ── Per-APP logical dumps: arriving, running, and restorable ──
  VIBE_APP_BACKUP_STALE,
  VIBE_APP_BACKUP_JOB_FAILING,
  VIBE_APP_RESTORE_DRILL_FAILED,
  VIBE_APP_RESTORE_DRILL_STALE,

  /**
   * ─────────────────────────────────────────────────────────────────────────
   * The tenant's own INFRASTRUCTURE backups — AWS Backup recovery points for
   * the Forgejo git-repo volume. A different subsystem from the four above.
   * ─────────────────────────────────────────────────────────────────────────
   *
   * 🔴 WHY THESE ARE NOT THE `VIBE_APP_BACKUP_*` CODES WITH A WIDER SCOPE. Those
   * four are per-APP and read an S3 object a `pg_dump` wrote. These are
   * per-TENANT and read an AWS Backup recovery point taken of a block device.
   * The entity differs (`vibe_app` vs `vibe_cluster`), the evidence differs, and
   * the fix differs: an app dump that stops is a job or a credential, a tenant
   * volume backup that stops is a plan, a vault or an IAM role. Sharing a code
   * would mean one alarm whose message can only describe one of them.
   */
  VIBE_TENANT_BACKUP_STALE,
  VIBE_TENANT_DR_COPY_STALE,
  VIBE_TENANT_BACKUP_JOB_FAILING,

  /**
   * ─────────────────────────────────────────────────────────────────────────
   * Whether those recovery points RECONSTITUTE — the restore drill.
   * ─────────────────────────────────────────────────────────────────────────
   * The three codes above all ask whether a backup ARRIVED. These two ask the
   * question that makes arriving worth anything: does the thing that arrived
   * come back as a usable disk with the tenant's git history on it.
   *
   * 🔴 WHY TWO CODES AND NOT ONE, and why the severities differ. They are not
   * two grades of the same alarm — they are different claims with different
   * evidence behind them. FAILED says we ran a restore and it did not produce
   * readable repositories: something IS broken, and the tenant's source history
   * may already be unrecoverable. STALE says nothing has proved it lately: it
   * asserts no defect at all, only that nobody can currently say. Giving them
   * the same severity would make the loud one easier to miss, which is exactly
   * backwards — a fleet where the "unproven" alarm is common would train
   * everyone to skim past the one that means the backups are gone.
   */
  VIBE_TENANT_RESTORE_DRILL_FAILED,
  VIBE_TENANT_RESTORE_DRILL_STALE,
  // Why a STALE above can say `neverProven` while every restore completes: the
  // validator that reads the restore back could not be launched. Forgejo only —
  // it is the one target whose drill has a read-back to launch.
  VIBE_TENANT_RESTORE_DRILL_VALIDATOR_LAUNCH_REFUSED,

  /**
   * ─────────────────────────────────────────────────────────────────────────
   * The same five questions, asked of the tenant's AURORA CLUSTER.
   * ─────────────────────────────────────────────────────────────────────────
   *
   * These are live: `VIBE_TENANT_BACKUP_TARGETS` carries an `aurora` row, so
   * both sweeps raise and retract all five. They were registered one change
   * BEFORE that row, which is what made adding it a row rather than a row plus
   * five registry files written under time pressure.
   *
   * The fleet-wide RPO policy they are judged under (`rpoHours: 30` /
   * `drRpoHours: 54`) is shared with the Forgejo row and has no per-target
   * lever. Aurora fits it measured rather than assumed — a daily plan at 03:30
   * UTC whose cross-region copy is an action on the same rule — which is why the
   * row was safe to add without reshaping the policy first.
   *
   * 🔴 FIVE CODES RATHER THAN REUSING THE FORGEJO FIVE. Issues are cleared by SET
   * DIFFERENCE over (code, entityType), and the entity for both targets is the
   * same tenant CLUSTER — so a shared code would let a healthy Forgejo volume
   * retract the Aurora alarm on the same tenant, silently, on every tick. The
   * codes are per-target because the entity cannot be.
   *
   * ⚠️ THE TWO DRILL CODES CARRY NO `operatorAction`, unlike their Forgejo twins.
   * `VIBE_RUN_TENANT_RESTORE_DRILL_NOW` is EBS-shaped end to end — it reads a
   * recovery point's volume type and size and passes an availability-zone
   * override — so it cannot dispatch an Aurora restore. A lever that refuses
   * every press is worse than none, so their remedies name the AWS-side steps
   * instead. The three freshness codes DO carry one:
   * `VIBE_RUN_TENANT_BACKUP_NOW` iterates the target table and matches the
   * protected resource by ARN, so it is resource-agnostic already.
   */
  VIBE_TENANT_AURORA_BACKUP_STALE,
  VIBE_TENANT_AURORA_DR_COPY_STALE,
  VIBE_TENANT_AURORA_BACKUP_JOB_FAILING,
  VIBE_TENANT_AURORA_RESTORE_DRILL_FAILED,
  VIBE_TENANT_AURORA_RESTORE_DRILL_MISCONFIGURED,
  VIBE_TENANT_AURORA_RESTORE_DRILL_STALE,

  // ── Is the tenant's git history on the tenant's own key ──
  VIBE_TENANT_FORGEJO_VOLUME_NOT_TENANT_ENCRYPTED,
  VIBE_FORGEJO_VOLUME_ENCRYPTION_UNOBSERVABLE,
  VIBE_TENANT_FORGEJO_VOLUME_AMBIGUOUS,
  VIBE_TENANT_FORGEJO_VOLUME_FAILED,

  // ── Does a READY VibeGitProject row still have a real repo on Forgejo ──
  VIBE_FORGEJO_REPOSITORY_MISSING,

  // ── Are audit events reaching the copy that outlives the database row ──
  VIBE_AUDIT_EXPORT_STALE,

  // ── A tenant's consumption against its soft caps: the band, then the crossing ──
  VIBE_COST_SAFETY_SOFT_LIMIT_APPROACHING,
  VIBE_COST_SAFETY_SOFT_LIMIT_CROSSED
} as const satisfies Record<string, IssueDefinition>;
